import { FC, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import FormSelect from "../../form-components/SelectComponent";
import { Badge, Button, Drawer, Table } from "antd";
import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { useDispatch } from "react-redux";
import { updateTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import { updateActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import { tasksApis } from "../../../api/NewTasks";
import { ColumnType } from "antd/es/table";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
import { phaseApis } from "../../../api/Phase";
import { projectsApis } from "../../../api/projects";
import { Breadcrumb, Dropdown, Flex, Space, Typography } from "antd";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";

const { Text } = Typography;

export interface TaskTagsInterface {
  tagId: number;
  tagName: string;
  taskTagId: number;
}
export type TaskStatus =
  | "Active"
  | "Inactive"
  | "On Hold"
  | "Closed"
  | "Archived"
  | "Deleted"
  | "In Progress"
  | "Draft"
  | "Pending Approval"
  | "Approval Rejected";

export type TaskFlagEnum = "Internal" | "External";
interface TaskTag {
  tagName: string;
}

interface TaskBoq {
  activityBoqId: number;
  allotedQuantity: number;
}

interface TaskResource {
  activityResourceId: number;
}

interface TaskUser {
  projectUserId: number;
  taskUserType: "Owner" | "Assignee" | string;
}

interface TaskItem {
  projectId: number;
  phaseId: number;
  activityId: number;
  taskName: string;
  taskDescription: string;
  taskOwnerId: number;
  taskActualStartDate: string;
  taskActualEndDate: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityId: number;
  isBillable: boolean;
  workHoursInSec: number;
  taskTags: TaskTag[];
  taskBoqs: TaskBoq[];
  taskResources: TaskResource[];
  taskUsers: TaskUser[];
  uid?: any;
  id?: any;
  taskId?: number;
  taskStatus?: string;
  taskOwnerName?: string; // Add taskOwnerName property
  priorityName?: string; // Add priorityName property
  //TODO: TASK ID to be received from response
}

const TaskListView = ({
  currentTab,
  id: activityId,
}: {
  currentTab: string;
  id: number | string;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: ActivityInDetailModal } = useSelector(
    (state: RootState) => state.ActivityInDetailModal
  );
  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );
  const {
    open,
    projectResponse,
    phaseResponse,
    activityName,
    allowEdit,
    allowAdd,
  }: any = ActivityInDetailModal || {};
  const projectIds = projectResponse?.id;
  const phaseIds = phaseResponse?.id;

  const [filtersModal, setFiltersModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [filterCount, setFilterCount] = useState(0);

  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Get access control permissions
  const { canView } = useAccessControl();

  const phaseName = phaseResponse?.name;
  const projectName = projectResponse?.name;

  const [tagFilterData, setTagFilterData] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [resourceHeadFiltersList, setresourceHeadFiltersList] = useState([]);
  const [groupBy, setGroupBy] = useState("taskList");

  const getLabel = useCallback(
    (list: any[], key: string) =>
      list.find((item) => item.key === key)?.label || key,
    []
  );

  const groupByOptions = useMemo(
    () => [
      {
        key: "taskList",
        label: "All Tasks",
      },
      {
        key: "today",
        label: "Today's tasks",
      },
      {
        key: "due-tommorrow",
        label: "Tasks due tomorrow",
      },
      {
        key: "next-7-days",
        label: "Tasks within Next 7 days",
      },
      {
        key: "overdue",
        label: "Overdue tasks",
      },
      {
        key: "today-and-overdue",
        label: "Today + Overdue tasks",
      },
    ],
    []
  );

  const columns: ColumnType<TaskItem>[] = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 250,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`Task-${record?.uid}`}
              onClick={() => {
                navigate(`/pms/project/task-detail/${record.taskId}`, {
                  state: {
                    ...location.state,
                    projectId: projectIds,
                    projectName,
                    phaseId: phaseIds,
                    phaseName,
                    activityId,
                    activityName,
                    taskId: record.taskId,
                    taskName: text,
                    taskStatus: record?.taskStatus,
                    returnPath: location.pathname,
                    returnState: location.state,
                  }
                });
              }}
            >
              {text}
            </span>
          </>
        );
      },
    },
    {
      title: "Owner",
      dataIndex: "taskOwnerName",
      key: "taskOwnerName",
      width: 250,
    },

    {
      title: "Planned Start Date",
      dataIndex: "taskPlannedStartDate",
      key: "taskPlannedStartDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "taskPlannedEndDate",
      key: "taskPlannedEndDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Priority",
      dataIndex: "priorityName",
      key: "priorityName",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "taskStatus",
      key: "taskStatus",
      width: 200,
      fixed: "right",
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });

  const {
    formState: { dirtyFields },
  } = methods;

  const [dataSource, setDataSource] = useState<any>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);

  const [endOfList, setEndOfList] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  // Add flag to prevent duplicate API calls
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  // Add ref to track last processed activityId to prevent duplicate calls
  const lastProcessedActivityId = useRef<number | string | null>(null);

  // Get location state safely
  const locationState = location.state as any;
  const locationExtraFields = locationState?.extra;
  const derivedProjectStatus = locationExtraFields?.screenName;

  // Function to handle filter submission
  const handleFilterSubmit = (data: any) => {
    // Get only dirty fields that were actually changed
    const dirtyData = getDirtyFieldsOnly(data, dirtyFields);

    // Count how many fields were actually changed (dirty)
    let count = dirtyData !== undefined ? Object.keys(dirtyData).length : 0;

    setFilterCount(count);
    setFiltersModal(false);

    // Refresh data with new filters
    refreshData(true, dirtyData);
  };

  // Function to reset filters
  const handleResetFilters = () => {
    methods.reset();
    setAppliedFilters({});
    setFilterCount(0);
    setFiltersModal(false);

    // Refresh data without filters
    refreshData(true, {});
  };

  const fetchData = async (params: any, append = false, filters: any = {}) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
        activityId: paramActivityId, // Get activityId from params to avoid closure issues
      } = params;

      // Determine mytaskFilterName based on groupBy selection
      let mytaskFilterName = null;
      if (groupBy !== "taskList") {
        mytaskFilterName = groupBy;
      }

      // Prepare filter parameters for API call
      const filterParams = {
        taskOwnerIds: filters.resourceHead
          ? filters.resourceHead.join(",")
          : undefined,
        tagIds: filters.tag ? filters.tag.join(",") : undefined,
        completionPercentage: filters.completion || undefined,
        taskStatus: filters.taskStatus || undefined,
        mytaskFilterName, // Add mytaskFilterName parameter
      };

      // Use the activityId from params if provided, otherwise fall back to component's activityId
      const currentActivityId = paramActivityId || activityId;

      const apiResponse: any = await tasksApis.listTasks({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds,
        phaseIds,
        activityIds: currentActivityId,
        derivedProjectStatus: derivedProjectStatus,
        ...filterParams, // Spread the filter parameters
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const {
          list,
          count,
          projectResponse,
          phaseResponse,
          activityResponse,
        } = data || {};
        let _allowEdit = true;
        const {
          status: projectStatus,
          isLocked,
          lockedBy,
        } = projectResponse || {};

        const { status: activityStatus } = activityResponse || {};

        const { status: phaseStatus } = phaseResponse || {};
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          // checking for project status
          _allowEdit = false;

        if (
          ["closed", "onhold", "archived"]?.includes(phaseStatus?.toLowerCase())
        )
          // checking for phase status
          _allowEdit = false;

        if (
          ["closed", "archived", "deleted"]?.includes(
            activityStatus?.toLowerCase()
          )
        )
          // checking for activity status
          _allowEdit = false;
        if (isLocked && lockedBy != currentUser?.id) {
          _allowEdit = false;
        } // checking for project lock

        // Separate permission for Add Task - less restrictive (anyone with module permission can add if conditions are met)
        let _allowAdd = true;
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        ) {
          _allowAdd = false;
        }
        if (
          ["closed", "onhold", "archived"]?.includes(phaseStatus?.toLowerCase())
        ) {
          _allowAdd = false;
        }
        if (
          ["closed", "archived", "deleted"]?.includes(
            activityStatus?.toLowerCase()
          )
        ) {
          _allowAdd = false;
        }
        if (isLocked && lockedBy != currentUser?.id) {
          _allowAdd = false;
        }

        dispatch(
          updateTaskListDetailsModalSlice({
            projectResponse: {
              ...projectResponse,
              name: projectName,
            },
            phaseResponse: {
              ...phaseResponse,
              name: phaseName,
            },
            activityResponse: {
              ...activityResponse,
              name: activityName,
            },
            allowEdit: _allowEdit,
          })
        );

        // Update ActivityInDetailModal with allowAdd for Add Task button
        dispatch(
          updateActivityInDetailModalSlice({
            allowAdd: _allowAdd,
          })
        );
        if (list.length < pageSize) {
          setEndOfList(true);
        }
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
      setIsRequestInProgress(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.taskId // Last item ID for backward direction
            : dataSource[0]?.taskId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData(
      {
        ...apiParams,
      },
      false,
      appliedFilters
    );
  };

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setDataSource([]);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      // Reset search query when switching activities
      setsearchQuery(null);
      // Reset request flag when switching activities
      setIsRequestInProgress(false);
      refreshData({
        pageDiff: 0,
        current: 1, // Force to start from page 1
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    if (open && currentTab === "tasks" && activityId) {
      // Prevent duplicate calls for the same activityId
      if (lastProcessedActivityId.current === activityId) {
        return;
      }

      // Update the last processed activityId
      lastProcessedActivityId.current = activityId;

      // Add a small delay to prevent race conditions when switching activities
      const timeoutId = setTimeout(() => {
        refreshListView();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    } else if (!open) {
      // Reset the last processed activityId when modal is closed
      lastProcessedActivityId.current = null;
    }
  }, [open, currentTab, activityId]);

  const refreshData = (overRideData?: any, filters: any = appliedFilters) => {
    fetchData(
      {
        oldPage: 1,
        current: (overRideData?.current || pagination.current).toString(),
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff:
          overRideData?.pageDiff !== undefined
            ? overRideData.pageDiff
            : pagination.current - 1,
        paginationId: null,
        searchQuery:
          overRideData?.searchQuery !== undefined
            ? overRideData.searchQuery
            : searchQuery,
        dateRange: "Quarter to Date",
        activityId: activityId, // Pass current activityId to prevent stale closure
        // customDateRange,
        ...overRideData,
      },
      false,
      filters
    );
  };

  // Refresh data when groupBy changes
  useEffect(() => {
    if (open && currentTab === "tasks" && activityId) {
      refreshData();
    }
  }, [groupBy]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const fetchTagFilterData = async () => {
    try {
      const response = await phaseApis.getTagsFilterData(
        projectIds?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.tagName,
            value: item.tagId,
          };
        });
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule("Phase");
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchResourceHeadFiltersList = async () => {
    try {
      const apiResponse = await projectsApis.getOwnersFilterData("Active");
      const { data, status } = apiResponse;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setresourceHeadFiltersList(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    if (filtersModal) {
      fetchTagFilterData();
      fetchStatusFilterData();
      fetchResourceHeadFiltersList();
    }
  }, [filtersModal]);

  const handleGroupByChange = useCallback((key: string) => {
    setGroupBy(key);
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, []);

  return (
    <>
      {canView("Tasks") && (
        <FormProvider {...methods}>
          <form>
            <div
              className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
              style={{ minHeight: "40px", border: "0px" }}
            >
              <div className="nk-ibx-head-actions w-45 ms-1">
                <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                  <div className={`w-100`}>
                    <SearchInput
                      name="search"
                      placeholder="Search..."
                      width="100%"
                      prefixIcon={<SearchOutlined />}
                      onDebouncedSearch={async (value: any) => {
                        setsearchQuery(value);
                        setPagination({
                          ...pagination,
                          current: 1,
                        });
                        await fetchData(
                          {
                            oldPage: 1,
                            current: 1,
                            sortOrder: "DESC",
                            pageSize: pagination.pageSize.toString(),
                            direction: "P",
                            pageDiff: 0,
                            paginationId: null,
                            searchQuery: value,
                            activityId: activityId, // Pass current activityId to prevent stale closure
                          },
                          false,
                          appliedFilters
                        );
                      }}
                      debounceDelay={300}
                    />
                  </div>
                </ul>
              </div>
              <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                <li className="pt-0 pb-0">
                  <Flex align="center" gap="14px">
                    <Breadcrumb>
                      <Breadcrumb.Item>
                        <span
                          style={{
                            marginRight: "8px",
                            fontWeight: 500,
                            color: "#2557d6",
                          }}
                        >
                          Task Period :
                        </span>
                        <Dropdown
                          menu={{
                            items: groupByOptions.map((item: any) => ({
                              key: item.key,
                              label: item.label,
                            })),
                            onClick: (item) => handleGroupByChange(item.key),
                            selectedKeys: [groupBy],
                          }}
                        >
                          <a onClick={(e) => e.preventDefault()}>
                            <Space>
                              <Text
                                style={{
                                  color: "#2557d6",
                                  fontWeight: 500,
                                }}
                              >
                                {getLabel(groupByOptions, groupBy)}
                              </Text>
                              <DownOutlined style={{ color: "#2557d6" }} />
                            </Space>
                          </a>
                        </Dropdown>
                      </Breadcrumb.Item>
                    </Breadcrumb>

                    <AddAccess moduleName="Tasks">
                      <Button
                        type="primary"
                        size="small"
                        disabled={!allowAdd}
                        onClick={() =>
                          // Full-page 4-step create flow, preselecting the
                          // activity currently in scope.
                          navigate(`/pms/project/task-create/${projectIds}`, {
                            state: {
                              id: projectIds,
                              projectName,
                              preselectedActivity: ActivityInDetailModal?.id
                                ? {
                                    activityId: Number(
                                      ActivityInDetailModal.id
                                    ),
                                    activityName,
                                    phaseId: phaseIds,
                                    phaseName,
                                  }
                                : undefined,
                            },
                          })
                        }
                      >
                        <Icon name={"plus"} />
                        <span>Add Task</span>
                      </Button>
                    </AddAccess>
                  </Flex>
                </li>
                <li>
                  <Button
                    type="text"
                    icon={<Icon name="redo" />}
                    onClick={() => {
                      refreshListView();
                    }}
                  />
                </li>

                <li className="me-n1">
                  <a
                    href="#filter"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setFiltersModal(true);
                    }}
                    className={`btn-icon me-2`}
                  >
                    <Badge color="geekblue" size="small" count={filterCount}>
                      <Icon style={{ fontSize: "20px" }} name="filter"></Icon>
                    </Badge>
                  </a>
                </li>
              </ul>
            </div>
          </form>
        </FormProvider>
      )}

      {canView("Tasks") ? (
        <div className="card-inner card-inner-lg p-0">
          <div className="content clearfix">
            <div
              style={{
                width: screenSize - 120 + "px",
                margin: "10px auto",
              }}
            >
              <SkeletonTable
                loading={loading}
                columns={columns as SkeletonTableColumnsType[]}
              >
                <Table
                  rowKey="uid"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={pagination}
                  onChange={tableOnChange}
                  bordered
                  size="middle"
                  scroll={{
                    x: 1100,
                  }}
                />
              </SkeletonTable>
            </div>
          </div>
        </div>
      ) : (
        <div className="card-inner card-inner-lg p-0">
          <div className="content clearfix">
            <div
              style={{
                width: screenSize - 120 + "px",
                margin: "10px auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <h5
                  style={{
                    color: "#666",
                    marginBottom: "16px",
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  No View Access
                </h5>
                <p
                  style={{
                    color: "#999",
                    fontSize: "14px",
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  You don't have permission to view tasks for this project.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  type="link"
                  size="small"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(handleFilterSubmit)}
              className="w-100"
            >
              <div key={"filters"} className="mb-2 mt-0">
                <FormMultiSelect
                  name={`resourceHead`}
                  label={"Resource Head"}
                  placeholder={"By Resource Head"}
                  options={resourceHeadFiltersList}
                />
              </div>

              <div key={"filters2"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`tag`}
                  label={"Tag"}
                  placeholder={"By Tag"}
                  options={tagFilterData}
                />
              </div>

              <div key={"filters3"} className="mt-0 mb-2">
                <FormSelect
                  name={`completion`}
                  label={"Completion %"}
                  placeholder={"By Completion %"}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const start = i * 5 + 1;
                    const end = (i + 1) * 5;
                    return {
                      value: `${start}-${end}`,
                      label: `${start}–${end}%`,
                    };
                  })}
                />
              </div>

              <div key={"filters4"} className="mt-0 mb-2">
                <FormSelect
                  name={`taskStatus`}
                  label={"Status"}
                  placeholder={"By Status"}
                  options={statusFilterData}
                />
              </div>

              <Space style={{ marginTop: 4 }}>
                <Button type="primary" size="small" htmlType="submit">
                  Find
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    methods.reset();
                    setFiltersModal(false);
                    setFilterCount(0);
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </form>
          </FormProvider>
        </div>
      </Drawer>
    </>
  );
};

export default TaskListView;
